import React, { FC, useEffect, useMemo, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useOktaAuth } from '@okta/okta-react'
import { useParams } from 'react-router-dom'
import {
	Button,
	ColumnLayout,
	Container,
	Form,
	Table,
	SpaceBetween,
	FormField,
	Input, // Ensure Input is imported for direct use
	TokenGroup,
	Popover,
	Box
} from '@cloudscape-design/components'
import FormSelect from '../../ControlledFormInputs/Select'
import FormInput from '../../ControlledFormInputs/Input'
import FormTagEditor from '../../ControlledFormInputs/TagEditor'
import useAcceptProject from '../../../hooks/mutate/useAcceptProject'
import useSingleProject from '../../../hooks/query/useSingleProject'
import useSingleAccount from '../../../hooks/query/useSingleAccount'
import useClusters from '../../../hooks/query/useClusters'
import { useApprovalFormStore } from '../../../stores/projects/approvalFormStore'
import type { Cluster } from '../../../types/cluster'
import type { Account } from '../../../types/account'
import type { ProjectApprovalRequest } from '../../../types/projects'
import { getClusterNetworkFromAccount } from '../../../helper/clusterAccountNetwork'
import {
	buildDiscoveryTagOptionsFromIpAllocations,
	buildSubnetAliasRowsFromIpAllocations,
	getAccountIpAddressAllocations,
	subnetAliasRowsArePlaceholders
} from '../../../helper/discoveryTagsFromAccountIpAllocations'
import {
	buildWorkloadClusterEksName,
	isWorkloadClusterRepoName,
	resolveClusterPortfolioPrefix
} from '../../../helper/clusterWorkloadRepo'

interface ApprovalFormProps {
	thisProjectID: string
	shouldShowApprovalForm: (shouldShow: boolean) => void
}

function toKebabCase(str: string): string {
	return str
		.toLowerCase()
		.replace(/\s+/g, '-')           // Replace spaces with hyphens
		.replace(/[^a-z0-9-]/g, '')     // Remove all chars except lowercase letters, digits, hyphens
		.replace(/-{2,}/g, '-')         // Collapse multiple consecutive hyphens into one
		.replace(/^-+|-+$/g, '')        // Trim leading/trailing hyphens
}

type ProjectTfProviderRow = {
	id: string
	alias: string
	region: string
	arn: string
	externalId: string
	/** Set for `workload` only; downstream templates render secretARN instead of arn. */
	secretARN?: string
}

type ProjectSubnetAliasRow = {
	id: string
	alias: string
	count: string
	identifier: string
	value: string
}

function breakableCellText(text: string) {
	return (
		<span style={{ wordBreak: 'break-all', overflowWrap: 'anywhere' }}>{text}</span>
	)
}

/** Aligns with project request / ApprovalDetails workload normalization */
function accountWorkloadKind(value: unknown): 'eks' | 'non-eks' | 'unknown' {
	if (value != null && typeof value === 'object' && 'value' in value) {
		return accountWorkloadKind((value as { value: unknown }).value)
	}
	const raw = String(value ?? '')
		.trim()
		.toLowerCase()
		.replace(/[_\s]+/g, '-')
	if (raw === 'eks') return 'eks'
	if (raw === 'non-eks' || (raw.includes('non') && raw.includes('eks'))) return 'non-eks'
	if (raw.includes('eks')) return 'eks'
	return 'unknown'
}

type EksSubAppRow = {
	id: string
	subAppName: string
	targetNamespace: string
}

function eksSubAppsFromStore(
	raw: unknown
): EksSubAppRow[] {
	if (!Array.isArray(raw) || raw.length === 0) return []
	return raw.map((r: Record<string, unknown>, i: number) => ({
		id: String(i + 1),
		subAppName: String(r.subAppName ?? r.name ?? ''),
		targetNamespace: String(r.targetNamespace ?? '')
	}))
}

function normalizeDistributionList(value: unknown): string {
	return String(value ?? '').trim().toLowerCase()
}

function projectEnvironmentScalar(
	details: Record<string, unknown> | undefined
): string {
	if (!details) return ''
	const e = details.environment
	if (e != null && typeof e === 'object' && 'value' in e) {
		return String((e as { value: unknown }).value).trim().toLowerCase()
	}
	return String(e ?? '').trim().toLowerCase()
}

function clusterEnvironmentScalar(env: unknown): string {
	return String(env ?? '').trim().toLowerCase()
}

function filterClustersForProject(
	clusters: Cluster[] | undefined,
	projectDetails: Record<string, unknown> | undefined
): Cluster[] {
	if (!clusters?.length || !projectDetails) return []
	const projectDl = normalizeDistributionList(projectDetails.distributionList)
	const projectEnv = projectEnvironmentScalar(projectDetails)
	if (!projectDl || !projectEnv) return []
	return clusters.filter((c) => {
		if (c.status !== 'APPROVED') return false
		if (normalizeDistributionList(c.details?.distributionList) !== projectDl) {
			return false
		}
		return clusterEnvironmentScalar(c.details?.environment) === projectEnv
	})
}

function clusterNamesEqual(a: unknown, b: unknown): boolean {
	return (
		String(a ?? '').trim().toLowerCase() === String(b ?? '').trim().toLowerCase()
	)
}

function resolveWorkloadClusterEksName(
	cluster: Cluster,
	portfolioPrefix: string
): string {
	const portfolio =
		resolveClusterPortfolioPrefix({
			portfolio: cluster.details?.portfolio,
			distributionList: cluster.details?.distributionList,
			tagProject: undefined
		}) || portfolioPrefix
	const application = String(cluster.details?.application ?? '')
		.trim()
		.toLowerCase()
	const environment = clusterEnvironmentScalar(cluster.details?.environment)
	return buildWorkloadClusterEksName({ portfolio, application, environment })
}

function findWorkloadClusterByName(
	clusters: Cluster[],
	name: string,
	portfolioPrefix = ''
): Cluster | undefined {
	const t = name.trim()
	if (!t) return undefined
	return clusters.find((c) => {
		if (clusterNamesEqual(c.details?.clusterName, t)) return true
		return clusterNamesEqual(
			resolveWorkloadClusterEksName(c, portfolioPrefix),
			t
		)
	})
}

function subnetRowsFromStore(
	aliases: Array<{
		alias?: string
		count?: string
		identifier?: string
		value?: string
	}> | undefined
): ProjectSubnetAliasRow[] {
	const list =
		aliases?.length && aliases.some((r) => r.alias || r.count || r.identifier || r.value)
			? aliases
			: [{ alias: '', count: '', identifier: '', value: '' }]
	return list.map((r, i) => ({
		id: String(i + 1),
		alias: String(r.alias ?? ''),
		count: String(r.count ?? ''),
		identifier: String(r.identifier ?? ''),
		value: String(r.value ?? '')
	}))
}

const ApprovalForm: FC<ApprovalFormProps> = ({
	thisProjectID,
	shouldShowApprovalForm
}) => {
	const { authState } = useOktaAuth()
	const thisUserEmail = authState?.idToken?.claims.email as string
	const { id } = useParams() as { id: string }
	const { data: thisProject } = useSingleProject(id, true)

	const projectWorkloadKind = useMemo(
		() => accountWorkloadKind(thisProject?.details?.accountWorkloadType),
		[thisProject?.details?.accountWorkloadType]
	)
	const approvalFormNeedsEksSections = projectWorkloadKind === 'eks'

	const { data: clustersList, isLoading: clustersLoading } = useClusters()

	const projectDetailsRecord = thisProject?.details as
		| Record<string, unknown>
		| undefined

	const matchingWorkloadClusters = useMemo(
		() => filterClustersForProject(clustersList, projectDetailsRecord),
		[clustersList, projectDetailsRecord]
	)

	const {
		formValues,
		actions: { setFormValues, clearFormValues }
	} = useApprovalFormStore()
	const { data: thisAccount, isLoading: accountLoading } = useSingleAccount(
		thisProject?.details?.distributionList,
		true
	)

	const projectEnvironment = projectEnvironmentScalar(projectDetailsRecord)

	const accountIpAllocations = useMemo(
		() =>
			getAccountIpAddressAllocations(
				thisAccount as Account | null | undefined,
				projectEnvironment
			),
		[thisAccount, projectEnvironment]
	)

	const accountIpAllocationsKey = useMemo(
		() =>
			accountIpAllocations
				.map(
					(row) =>
						`${row.label}|${row.cidrAzA ?? ''}|${row.cidrAzB ?? ''}|${row.cidrAzC ?? ''}`
				)
				.join(';'),
		[accountIpAllocations]
	)

	const {
		control,
		trigger,
		setValue,
		getValues,
		formState: { isValid }
	} = useForm({
		mode: 'onChange',
		reValidateMode: 'onChange',
		defaultValues: formValues
	})

	const workloadClusterNameWatch = useWatch({
		control,
		name: 'workloadClusterName',
		defaultValue: formValues.workloadClusterName ?? ''
	})

	/**
	 * The approval store is a module-level singleton and was only reset after a successful
	 * approve, so a form abandoned on one project seeded `useForm` defaultValues on the next
	 * one (a stale Workload Cluster Name that only a hard refresh cleared). Reset on unmount
	 * so every approval form starts from the store's initial values.
	 */
	useEffect(() => {
		return () => {
			clearFormValues()
		}
	}, [clearFormValues])

	const discoveryTagSelectOptions = useMemo(
		() => buildDiscoveryTagOptionsFromIpAllocations(accountIpAllocations),
		[accountIpAllocations]
	)

	const discoveryTagOptionsKey = useMemo(
		() => discoveryTagSelectOptions.map((o) => o.value).join('|'),
		[discoveryTagSelectOptions]
	)

	const { mutate: acceptProject, isLoading } = useAcceptProject(thisProjectID)

	// DEFINE useState HOOKS AT THE TOP OF THE COMPONENT
	const [text, setText] = useState('')
	const [tokenItems, setTokenItems] = useState<
		Array<{ label: string; dismissLabel: string }>
	>([])
	// New state to track if KMS fields are auto-populated
	const [isKMSAutoPopulated, setIsKMSAutoPopulated] = useState(false)
	const [accountInfraLocks, setAccountInfraLocks] = useState({
		dnsZoneApex: false
	})

	const [eksNsText, setEksNsText] = useState('')
	const [eksCustomNamespaces, setEksCustomNamespaces] = useState<string[]>(
		() =>
			Array.isArray(formValues.eksCustomNamespaces)
				? [...formValues.eksCustomNamespaces]
				: []
	)
	const [eksSubAppsItems, setEksSubAppsItems] = useState<EksSubAppRow[]>(() =>
		eksSubAppsFromStore(formValues.eksSubApps)
	)

	const projectApplication = useMemo(() => {
		const systemAlias =
			thisProject?.tags?.details?.systemAlias?.value ??
			thisProject?.tags?.details?.systemAlias ??
			''
		return typeof systemAlias === 'string'
			? systemAlias.trim().toLowerCase()
			: String(systemAlias).trim().toLowerCase()
	}, [thisProject?.tags?.details?.systemAlias])

	const portfolioPrefix = useMemo(
		() =>
			resolveClusterPortfolioPrefix({
				distributionList: thisProject?.details?.distributionList,
				tagProject: thisProject?.tags?.details?.tagProject
			}),
		[thisProject?.details?.distributionList, thisProject?.tags?.details?.tagProject]
	)

	const { vpcName: vpcValue, privateHostedZoneName: dnsZoneApexValue } = useMemo(
		() =>
			getClusterNetworkFromAccount(
				thisAccount as Account | null | undefined,
				projectEnvironment,
				projectApplication,
				{
					portfolioPrefix,
					systemAlias: projectApplication,
					distributionList: thisProject?.details?.distributionList,
					accountType: thisAccount?.type,
					isStgFlow: projectEnvironment === 'st'
				}
			),
		[
			thisAccount,
			projectEnvironment,
			projectApplication,
			portfolioPrefix,
			thisProject?.details?.distributionList
		]
	)

	useEffect(() => {
		const resetLocks = { dnsZoneApex: false }
		if (!thisAccount || thisAccount.status === 'ERROR') {
			setAccountInfraLocks(resetLocks)
			return
		}

		const locks = { ...resetLocks }
		if (dnsZoneApexValue) {
			setValue('dnsZoneApex', dnsZoneApexValue)
			setFormValues('dnsZoneApex', dnsZoneApexValue)
			locks.dnsZoneApex = true
		}
		if (vpcValue) {
			setValue('vpcIdentifier', 'tag:Name')
			setFormValues('vpcIdentifier', 'tag:Name')
			setValue('vpcValue', vpcValue)
			setFormValues('vpcValue', vpcValue)
		}
		setAccountInfraLocks(locks)
	}, [thisAccount, dnsZoneApexValue, vpcValue, setValue, setFormValues])

	useEffect(() => {
		if (!approvalFormNeedsEksSections) return
		if (clustersLoading) return
		if (matchingWorkloadClusters.length !== 1) return
		const c = matchingWorkloadClusters[0]
		const nm = resolveWorkloadClusterEksName(c, portfolioPrefix)
		if (!nm) return
		const current = String(getValues('workloadClusterName') ?? '').trim()
		if (current && !isWorkloadClusterRepoName(current)) return
		if (current === nm) return
		setValue('workloadClusterName', nm)
		setFormValues('workloadClusterName', nm)
	}, [
		approvalFormNeedsEksSections,
		clustersLoading,
		matchingWorkloadClusters,
		portfolioPrefix,
		setValue,
		setFormValues,
		getValues
	])

	useEffect(() => {
		if (!approvalFormNeedsEksSections) return
		const nm = String(workloadClusterNameWatch ?? '').trim()
		const cluster = nm
			? findWorkloadClusterByName(matchingWorkloadClusters, nm, portfolioPrefix)
			: undefined
		const raw = cluster?.approval?.workloadClusterVersion
		const verStr =
			raw != null && String(raw).trim() !== '' ? String(raw).trim() : ''
		const current = String(getValues('workloadClusterVersion') ?? '').trim()
		if (verStr === current) return
		setValue('workloadClusterVersion', verStr)
		setFormValues('workloadClusterVersion', verStr)
	}, [
		approvalFormNeedsEksSections,
		workloadClusterNameWatch,
		matchingWorkloadClusters,
		portfolioPrefix,
		setValue,
		setFormValues,
		getValues
	])

	useEffect(() => {
		if (!approvalFormNeedsEksSections) return
		void trigger('workloadClusterVersion')
	}, [approvalFormNeedsEksSections, workloadClusterNameWatch, trigger])

	useEffect(() => {
		if (approvalFormNeedsEksSections) return
		setValue('workloadClusterName', '')
		setValue('workloadClusterVersion', '')
		setValue('discoveryTags', null)
		setFormValues('workloadClusterName', '')
		setFormValues('workloadClusterVersion', '')
		setFormValues('discoveryTags', null)
		setFormValues('eksCustomNamespaces', [])
		setFormValues('eksSubApps', [])
		setEksCustomNamespaces([])
		setEksSubAppsItems([])
		setEksNsText('')
	}, [approvalFormNeedsEksSections, setValue, setFormValues])

	useEffect(() => {
		if (!approvalFormNeedsEksSections) return
		setFormValues('eksCustomNamespaces', eksCustomNamespaces)
	}, [approvalFormNeedsEksSections, eksCustomNamespaces, setFormValues])

	useEffect(() => {
		if (!approvalFormNeedsEksSections) return
		setFormValues(
			'eksSubApps',
			eksSubAppsItems.map(({ subAppName, targetNamespace }) => ({
				subAppName,
				targetNamespace
			}))
		)
	}, [approvalFormNeedsEksSections, eksSubAppsItems, setFormValues])

	/**
	 * TFProviders differ by workload type — restores the split from TKLO-1751 (ccb8eac5),
	 * which was lost when 04db8dac rewrote this form and emitted one flat list for both.
	 *
	 * Sets match the dominant pattern across the Atlas project configs:
	 *   non-EKS (6) = base
	 *   EKS     (8) = base + workload + sharedservices
	 *
	 * ARNs are intentionally left as-is: `instance_scheduler` points at the STG shared-services
	 * account in every environment (including pd) and `sharedservices` at the PRD one, which is
	 * what the generated configs actually contain.
	 */
	const staticTFProvidersDefinition = useMemo(
		() => [
			{
				alias: 'environment',
				region: 'ap-southeast-1',
				arn: `arn:aws:iam::ACCOUNT_ID:role/AC2-IRS-DO-GLI-BL-ACCOUNT_ID-VALUE`,
				externalID: `GitlabRunner-426956141294-ACCOUNT_ID`
			},
			{
				alias: 'route53',
				region: 'ap-southeast-1',
				arn: `arn:aws:iam::ACCOUNT_ID:role/AC2-IRS-DO-GLI-BL-ACCOUNT_ID-VALUE`,
				externalID: `GitlabRunner-426956141294-ACCOUNT_ID`
			},
			{
				alias: 'dr',
				region: 'ap-southeast-2',
				arn: `arn:aws:iam::ACCOUNT_ID:role/AC2-IRS-DO-GLI-BL-ACCOUNT_ID-VALUE`,
				externalID: `GitlabRunner-426956141294-ACCOUNT_ID`
			},
			{
				alias: 'security',
				region: 'ap-southeast-1',
				arn: `arn:aws:iam:::role/AC2-IRS-DO-GLD-SECURITY-VALUE`,
				externalID: 'atlas'
			},
			{
				alias: 'security_dr',
				region: 'ap-southeast-2',
				arn: `arn:aws:iam:::role/AC2-IRS-DO-GLD-SECURITY-VALUE`,
				externalID: 'atlas'
			},
			{
				alias: 'instance_scheduler',
				region: 'ap-southeast-1',
				arn: `arn:aws:iam::765885332192:role/AC2-IRS-DO-GLD-ATLAS_SHARED_SERVICES-STG`,
				externalID: 'atlas'
			},
			...(approvalFormNeedsEksSections
				? [
						{
							alias: 'workload',
							region: 'ap-southeast-1',
							arn: 'arn:aws:secretsmanager:ap-southeast-1:713010294205:secret:/atlas/gitlab/projects/cluster_manifests_workload_token',
							secretARN:
								'arn:aws:secretsmanager:ap-southeast-1:713010294205:secret:/atlas/gitlab/projects/cluster_manifests_workload_token',
							externalID: ''
						},
						{
							alias: 'sharedservices',
							region: 'ap-southeast-1',
							arn: 'arn:aws:iam::337994588368:role/AC2-IRS-DO-GLD-ATLAS_SHARED_SERVICES-PRD',
							externalID: 'atlas'
						}
					]
				: [])
		],
		[approvalFormNeedsEksSections]
	)

	const defaultTfProviders = useMemo((): ProjectTfProviderRow[] => {
		const awsAccountID = thisProject?.details?.awsAccountID ?? ''
		const KMSawsAccountID = formValues.awsAccountId?.value || ''
		const environment = thisProject?.details?.environment?.value
		let envValue = ''
		if (environment === 'pd') {
			envValue = 'PRD'
		} else if (environment === 'dv' || environment === 'st') {
			envValue = 'STG'
		}
		const securityAccountId =
			environment === 'pd' ? '713010294205' : '085094121557'
		// SharedServices is split per service tier: prod projects assume the PRD role,
		// everything else the STG one. `sharedservices` stays pinned to PRD by design.
		const instanceSchedulerAccountId =
			environment === 'pd' ? '337994588368' : '765885332192'

		return staticTFProvidersDefinition.map((def, index) => {
			let resolvedArn = def.arn
			let resolvedExternalID = def.externalID
			if (def.alias === 'security' || def.alias === 'security_dr') {
				resolvedArn = `arn:aws:iam::${securityAccountId}:role/AC2-IRS-DO-GLD-SECURITY-${envValue}`
			} else if (def.alias === 'instance_scheduler') {
				resolvedArn = `arn:aws:iam::${instanceSchedulerAccountId}:role/AC2-IRS-DO-GLD-ATLAS_SHARED_SERVICES-${envValue}`
			} else if (
				def.alias === 'sharedservices' ||
				def.alias === 'workload'
			) {
				// fixed templates
			} else {
				resolvedArn = resolvedArn
					.replace(/ACCOUNT_ID/g, awsAccountID || '')
					.replace(/KMS_ACCOUNT_ID/g, KMSawsAccountID)
					.replace(/VALUE/g, envValue)
				resolvedExternalID = resolvedExternalID.replace(
					/ACCOUNT_ID/g,
					awsAccountID || ''
				)
			}
			return {
				id: String(index + 1),
				alias: def.alias,
				region: def.region,
				arn: resolvedArn,
				externalId: resolvedExternalID,
				...('secretARN' in def ? { secretARN: def.secretARN } : {})
			}
		})
	}, [
		staticTFProvidersDefinition,
		thisProject?.details?.awsAccountID,
		formValues.awsAccountId?.value,
		thisProject?.details?.environment?.value
	])

	const [additionalTfProviders, setAdditionalTfProviders] = useState<
		ProjectTfProviderRow[]
	>([])

	const tfProvidersItems = useMemo(
		() => [...defaultTfProviders, ...additionalTfProviders],
		[defaultTfProviders, additionalTfProviders]
	)

	const [subnetAliasesItems, setSubnetAliasesItems] = useState<ProjectSubnetAliasRow[]>(
		() => subnetRowsFromStore(formValues.subnetAliases)
	)

	useEffect(() => {
		setFormValues(
			'subnetAliases',
			subnetAliasesItems.map(({ alias, count, identifier, value }) => ({
				alias,
				count,
				identifier,
				value
			}))
		)
	}, [subnetAliasesItems, setFormValues])

	useEffect(() => {
		if (accountLoading) return
		if (!accountIpAllocationsKey) return

		const inherited = buildSubnetAliasRowsFromIpAllocations(accountIpAllocations)
		if (inherited.length === 0) return

		setSubnetAliasesItems((prev) => {
			// Only auto-fill when the table is still empty placeholders; never clobber edits.
			if (!subnetAliasRowsArePlaceholders(prev)) return prev
			return inherited.map((row, index) => ({
				id: String(index + 1),
				alias: row.alias,
				count: row.count,
				identifier: row.identifier,
				value: row.value
			}))
		})
	}, [accountLoading, accountIpAllocations, accountIpAllocationsKey])

	useEffect(() => {
		if (!approvalFormNeedsEksSections) return
		if (accountLoading) return
		const cur = getValues('discoveryTags') as { value?: string } | null
		if (!cur?.value) return
		const ok = discoveryTagSelectOptions.some((o) => o.value === cur.value)
		if (ok) return
		setValue('discoveryTags', null)
		setFormValues('discoveryTags', null)
	}, [
		approvalFormNeedsEksSections,
		accountLoading,
		discoveryTagOptionsKey,
		discoveryTagSelectOptions,
		getValues,
		setValue,
		setFormValues
	])

	useEffect(() => {
		if (!approvalFormNeedsEksSections) return
		void trigger('discoveryTags')
	}, [
		approvalFormNeedsEksSections,
		discoveryTagOptionsKey,
		workloadClusterNameWatch,
		trigger
	])

	useEffect(() => {
		// Use project system alias tag as the primary auto input value
		const systemAlias =
			thisProject?.tags?.details?.systemAlias?.value ||
			thisProject?.tags?.details?.systemAlias ||
			''
		
		const kebabCaseName = toKebabCase(
			typeof systemAlias === 'string' ? systemAlias : String(systemAlias)
		)

		setValue('platformName', kebabCaseName)
		setFormValues('platformName', kebabCaseName)
	}, [
		setValue,
		setFormValues,
		thisProject?.tags?.details?.systemAlias
	])

	// NEW useEffect for Environment-based KMS defaults (Aws Account Id & Delegate AWSAccount IDs)
	// This hook runs when the project's environment changes to auto-populate KMS fields.
	useEffect(() => {
		const environment = thisProject?.details?.environment?.value // Get the environment value
		const awsAccountID = thisProject?.details?.awsAccountID
		let defaultAccountId = ''
		let shouldAutoPopulate = false // Flag to determine if KMS fields should be disabled

		// Determine which AWS Account Id to use based on the environment
		if (environment === 'dv' || environment === 'st') {
			defaultAccountId = '085094121557'
			shouldAutoPopulate = true
		} else if (environment === 'pd') {
			defaultAccountId = '713010294205'
			shouldAutoPopulate = true
		}

		// Set the state to disable/enable KMS fields
		setIsKMSAutoPopulated(shouldAutoPopulate)

		// 1. Auto-populate 'Aws Account Id' (FormSelect)
		if (defaultAccountId) {
			setValue('awsAccountId', {
				label: defaultAccountId,
				value: defaultAccountId
			})
			setFormValues('awsAccountId', {
				label: defaultAccountId,
				value: defaultAccountId
			})
		} else {
			setValue('awsAccountId', undefined)
			setFormValues('awsAccountId', undefined)
		}

		// 2. Auto-populate 'Delegate AWSAccount IDs' (TokenGroup)
		if (defaultAccountId) {
			const delegateAccountIDs = awsAccountID
				? [defaultAccountId, awsAccountID]
				: [defaultAccountId]
			const tokens = delegateAccountIDs.map((id) => ({
				label: id,
				dismissLabel: `Remove item ${id}`
			}))
			setTokenItems(tokens)
		} else {
			setTokenItems([])
		}
	}, [
		thisProject?.details?.environment?.value,
		thisProject?.details?.awsAccountID,
		setValue,
		setFormValues,
		setTokenItems,
		setIsKMSAutoPopulated
	])

	// Existing useEffect for Role Name auto-population
	// This hook runs when 'awsAccountId' changes (either manually or by the new useEffect above)
	// It correctly sets the 'roleName' based on the 'awsAccountId' value.
	useEffect(() => {
		const selectedAccountId = formValues.awsAccountId?.value // Get the actual value of the selected AWS Account Id
		let roleName = ''
		if (selectedAccountId && selectedAccountId.includes('085094121557')) {
			// Matches the actual value for 'dv'/'st' related option
			roleName = 'arn:aws:iam::085094121557:role/AC2-IRS-DO-GLD-SECURITY-STG'
		} else if (
			selectedAccountId &&
			selectedAccountId.includes('713010294205')
		) {
			// Matches the actual value for 'pd' related option
			roleName = 'arn:aws:iam::713010294205:role/AC2-IRS-DO-GLD-SECURITY-PRD'
		}
		setValue('roleName', roleName)
		setFormValues('roleName', roleName)
	}, [setValue, setFormValues, formValues.awsAccountId?.value])

	const _handleApprove = () => {
		trigger()

		if (!isValid) return

		const transformedProviders = tfProvidersItems.map(
			({ alias, region, arn, externalId, secretARN }) => ({
				alias,
				region,
				arn,
				externalId,
				// Templates branch on `secretARN is defined`; only `workload` carries one.
				...(secretARN ? { secretARN } : {})
			})
		)

		const details: Record<string, unknown> = {
			...formValues,
			vpcIdentifier: 'tag:Name',
			vpcValue,
			dnsZoneApex: String(getValues('dnsZoneApex') ?? dnsZoneApexValue ?? '').trim(),
			tfProviders: transformedProviders,
			commit: false
		}
		if (approvalFormNeedsEksSections) {
			let name = String(getValues('workloadClusterName') ?? '').trim()
			const cluster = findWorkloadClusterByName(
				matchingWorkloadClusters,
				name,
				portfolioPrefix
			)
			if (cluster) {
				const eksName = resolveWorkloadClusterEksName(cluster, portfolioPrefix)
				if (eksName) name = eksName
			}
			details.workloadClusterName = name
			details.workloadClusterWorkloadRepoNameEnv = cluster
				? cluster.workloadRepoNameEnv ?? cluster.clusterID
				: ''
			details.eksCustomNamespaces = [...eksCustomNamespaces]
			details.eksSubApps = eksSubAppsItems.map(({ subAppName, targetNamespace }) => ({
				subAppName,
				targetNamespace
			}))
		}
		const eksOnlyKeys = [
			'workloadClusterName',
			'workloadClusterWorkloadRepoNameEnv',
			'workloadClusterVersion',
			'discoveryTags',
			'eksCustomNamespaces',
			'eksSubApps',
			'snapshot'
		] as const
		if (!approvalFormNeedsEksSections) {
			for (const k of eksOnlyKeys) {
				delete details[k]
			}
		}

		const approvalRequest: ProjectApprovalRequest = {
			action: 'APPROVE',
			projectID: thisProjectID,
			approverID: thisUserEmail,
			details: details as ProjectApprovalRequest['details']
		}

		acceptProject(approvalRequest, {
			onSuccess: () => {
				shouldShowApprovalForm(false)
				clearFormValues()
			}
		})
	}

	useEffect(() => {
		setFormValues(
			'delegateAWSAccountIDs',
			tokenItems.map((item) => item.label)
		)
	}, [tokenItems, setFormValues])

	return (
		<Form
			actions={
				<SpaceBetween direction="horizontal" size="xs">
					<Button variant="link" onClick={() => shouldShowApprovalForm(false)}>
						Cancel
					</Button>
					<Button
						variant="primary"
						loading={isLoading}
						onClick={_handleApprove}
					>
						Approve
					</Button>
				</SpaceBetween>
			}
		>
			<Container>
				<SpaceBetween direction="vertical" size="l">
					{approvalFormNeedsEksSections && (
						<SpaceBetween direction="vertical" size="l">
							<p
								style={{
									fontWeight: 'bold',
									fontSize: '1.1em',
									marginBottom: '-10px'
								}}
							>
								Cluster Details
							</p>
							<ColumnLayout columns={2} variant="text-grid">
								<FormInput
									name="workloadClusterName"
									label="Workload Cluster Name"
									control={control}
									setFormValues={setFormValues}
									disabled
									placeholder={
										clustersLoading
											? 'Loading clusters…'
											: matchingWorkloadClusters.length === 0
												? 'No matching approved clusters'
												: matchingWorkloadClusters.length === 1
													? 'Set from matching cluster'
													: '—'
									}
									rules={{
										required: 'Workload cluster name is required',
										validate: (v: string) => {
											if (clustersLoading) return true
											if (matchingWorkloadClusters.length === 0) {
												return 'No approved clusters match this project distribution list and environment.'
											}
											if (matchingWorkloadClusters.length > 1) {
												return 'More than one approved cluster matches; workload cluster name is only set when there is a single match.'
											}
											const t = String(v ?? '').trim()
											if (!t) return true
											return findWorkloadClusterByName(
												matchingWorkloadClusters,
												t,
												portfolioPrefix
											)
												? true
												: 'Must match an approved cluster name for this project distribution list and environment.'
										}
									}}
									info={
										<Popover
											header="Workload Cluster Name"
											content={
												<Box variant="small">
													This value is read-only. When exactly one approved cluster
													matches this project’s distribution list and environment,
													the EKS workload cluster name is filled automatically
													(for example ntg-bbsdt-nonprod-wl-dv-kc-1). Workload
													cluster version is taken from that cluster’s approval
													record.
												</Box>
											}
											triggerType="custom"
										>
											<Button
												variant="icon"
												iconName="status-info"
												ariaLabel="Info about Workload Cluster Name"
											/>
										</Popover>
									}
								/>
								<FormInput
									name="workloadClusterVersion"
									label="Workload Cluster Version"
									control={control}
									setFormValues={setFormValues}
									disabled
									placeholder="Inherited from cluster approval"
									rules={{
										validate: () => {
											if (clustersLoading || matchingWorkloadClusters.length === 0) {
												return true
											}
											const name = String(
												getValues('workloadClusterName') ?? ''
											).trim()
											if (!name) return true
											const c = findWorkloadClusterByName(
												matchingWorkloadClusters,
												name,
												portfolioPrefix
											)
											if (!c) return true
											const ver = c.approval?.workloadClusterVersion
											if (ver != null && String(ver).trim() !== '') return true
											return 'This cluster has no approved workload version in SSP.'
										}
									}}
								/>
							</ColumnLayout>
							<FormSelect
								name="discoveryTags"
								label="Discovery Tags"
								control={control}
								setFormValues={setFormValues}
								options={discoveryTagSelectOptions}
								disabled={
									accountLoading || discoveryTagSelectOptions.length === 0
								}
								statusType={accountLoading ? 'loading' : undefined}
								placeholder={
									accountLoading
										? 'Loading…'
										: discoveryTagSelectOptions.length === 0
											? 'No IP address allocations on account network approval'
											: 'Select discovery tag'
								}
								info={
									<Popover
										header="Discovery Tags"
										content={
											<Box variant="small">
												Options are IP address allocation rows from the
												account&apos;s network approval (Workernode,
												Shared-Workernode, etc.). Description shows configured
												CIDRs per availability zone.
											</Box>
										}
										triggerType="custom"
									>
										<Button
											variant="icon"
											iconName="status-info"
											ariaLabel="Info about Discovery Tags"
										/>
									</Popover>
								}
								rules={{
									validate: (v: { value?: string } | null) => {
										if (accountLoading) return true
										if (discoveryTagSelectOptions.length === 0) {
											return 'Account network approval has no IP address allocations; configure them on the account before approving this project.'
										}
										return v?.value
											? true
											: 'Select a discovery tag'
									}
								}}
							/>

							<p
								style={{
									fontWeight: 'bold',
									fontSize: '1.1em',
									marginBottom: '-10px'
								}}
							>
								App of Apps
							</p>
							<FormField label="Custom NameSpaces">
								<SpaceBetween direction="vertical" size="s">
									<div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
										<div style={{ flex: 1, minWidth: 0 }}>
											<Input
												value={eksNsText}
												onChange={({ detail }) => setEksNsText(detail.value)}
												onKeyDown={({ detail }) => {
													if (detail.key !== 'Enter') return
													const t = eksNsText.trim()
													if (!t) return
													setEksCustomNamespaces((prev) => [...prev, t])
													setEksNsText('')
												}}
												placeholder="Namespace name"
											/>
										</div>
										<Button
											variant="normal"
											onClick={() => {
												const t = eksNsText.trim()
												if (!t) return
												setEksCustomNamespaces((prev) => [...prev, t])
												setEksNsText('')
											}}
										>
											Add Namespaces
										</Button>
									</div>
									{eksCustomNamespaces.length > 0 && (
										<TokenGroup
											items={eksCustomNamespaces.map((n) => ({
												label: n,
												dismissLabel: `Remove ${n}`
											}))}
											onDismiss={({ detail: { itemIndex } }) => {
												setEksCustomNamespaces((prev) =>
													prev.filter((_, i) => i !== itemIndex)
												)
											}}
										/>
									)}
								</SpaceBetween>
							</FormField>

							<FormField label="Sub Apps">
								<SpaceBetween direction="vertical" size="s">
									{eksSubAppsItems.map((row) => (
										<div
											key={row.id}
											style={{
												display: 'flex',
												gap: '10px',
												alignItems: 'flex-end',
												width: '100%',
												flexWrap: 'wrap'
											}}
										>
											<div style={{ flex: 1, minWidth: 140 }}>
												<Box variant="awsui-key-label">Sub App Name</Box>
												<Input
													value={row.subAppName}
													onChange={({ detail }) =>
														setEksSubAppsItems((prev) =>
															prev.map((p) =>
																p.id === row.id
																	? { ...p, subAppName: detail.value }
																	: p
															)
														)
													}
													placeholder="Sub app name"
												/>
											</div>
											<div style={{ flex: 1, minWidth: 140 }}>
												<Box variant="awsui-key-label">Target Namespace</Box>
												<Input
													value={row.targetNamespace}
													onChange={({ detail }) =>
														setEksSubAppsItems((prev) =>
															prev.map((p) =>
																p.id === row.id
																	? { ...p, targetNamespace: detail.value }
																	: p
															)
														)
													}
													placeholder="e.g. default"
												/>
											</div>
											<Button
												variant="inline-icon"
												iconName="remove"
												ariaLabel="Remove sub app"
												onClick={() =>
													setEksSubAppsItems((prev) =>
														prev.filter((p) => p.id !== row.id)
													)
												}
											>
												Remove
											</Button>
										</div>
									))}
									<Box margin={{ top: 's' }}>
										<Button
											variant="normal"
											onClick={() =>
												setEksSubAppsItems((prev) => [
													...prev,
													{
														id: `subapp-${Date.now()}`,
														subAppName: '',
														targetNamespace: ''
													}
												])
											}
										>
											Add Sub App
										</Button>
									</Box>
								</SpaceBetween>
							</FormField>
						</SpaceBetween>
					)}

					<FormField label="VPC Aliases:">
						<ColumnLayout columns={2} variant="text-grid">
							<div>
								<Box variant="awsui-key-label">VPC Identifier</Box>
								<Input value="tag:Name" readOnly disabled />
							</div>
							<div>
								<Box variant="awsui-key-label">VPC Value</Box>
								<Input value={vpcValue} readOnly disabled />
							</div>
						</ColumnLayout>
					</FormField>

					{/* DNS Zone Apex - Tooltip placement restored to FormField info prop */}
					<FormInput
						name="dnsZoneApex"
						label="DNS Zone Apex"
						control={control}
						setFormValues={setFormValues}
						disabled={accountInfraLocks.dnsZoneApex}
						rules={{ required: 'Required' }}
						info={
							<Popover
								header="DNS Zone Apex"
								content={
									<Box variant="small">
										The top-level domain name for your project's DNS records
										(e.g., 'example.com').
									</Box>
								}
								triggerType="custom"
							>
								<Button
									variant="icon"
									iconName="status-info"
									ariaLabel="Info about DNS Zone Apex"
								/>
							</Popover>
						}
					/>

					{/* Platform Name - Tooltip placement restored to FormField info prop */}
					<FormInput
						name="platformName"
						label="Platform Name"
						control={control}
						setFormValues={setFormValues}
						rules={{
							required: 'Required',
							pattern: {
								value: /^[a-z0-9-]+$/,
								message:
									'Only small alphabetics, numerics, and hyphens are allowed with no spaces.'
							}
						}}
						disabled={true}
						info={
							<Popover
								header="Platform Name"
								content={
									<Box variant="small">
										The unique identifier for the platform, derived from the
										account's subgroup name (read-only).
									</Box>
								}
								triggerType="custom"
							>
								<Button
									variant="icon"
									iconName="status-info"
									ariaLabel="Info about Platform Name"
								/>
							</Popover>
						}
					/>

					<p
						style={{
							fontWeight: 'bold',
							fontSize: '1.1em',
							marginBottom: '-10px'
						}}
					>
						KMS:
					</p>
					{/* AWS Account Id - Conditional rendering for disabled state */}
					<div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px' }}>
						<div style={{ flexGrow: 1 }}>
							{isKMSAutoPopulated ? (
								<FormField // Use FormField here for label and info icon
									label="Aws Account Id" // FormField provides the label
									info={
										<Popover
											header="AWS Account ID"
											content={
												<Box variant="small">
													AWS account ID auto-populated based on environment.
												</Box>
											}
											triggerType="custom"
										>
											<Button
												variant="icon"
												iconName="status-info"
												ariaLabel="Info about AWS Account ID"
											/>
										</Popover>
									}
								>
									<Input // Use plain Cloudscape Input for fixed display
										value={formValues.awsAccountId?.value || ''} // Directly access the string value
										disabled={true} // Always disabled
										onChange={() => { }} // Provide an empty onChange for a disabled Input
									/>
								</FormField>
							) : (
								<FormSelect
									label="Aws Account Id"
									name="awsAccountId"
									control={control}
									setFormValues={setFormValues}
									options={[
										{ label: '085094121557', value: '085094121557' },
										{ label: '713010294205', value: '713010294205' }
									]}
									placeholder="Select an option"
								/>
							)}
						</div>
						{/* Popover button for Aws Account Id is now handled inside FormField */}
					</div>

					{/* Role Name - Always disabled when auto-populated */}
					<div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px' }}>
						<div style={{ flexGrow: 1 }}>
							<FormInput
								label="Role Name"
								name="roleName"
								control={control}
								setFormValues={setFormValues}
								disabled={isKMSAutoPopulated}
							/>
						</div>
						<Popover
							header="Role Name"
							content={
								<Box variant="small">
									The IAM role name to be used for KMS operations, pre-populated
									based on the selected AWS Account ID.
								</Box>
							}
							triggerType="custom"
						>
							<Button
								variant="icon"
								iconName="status-info"
								ariaLabel="Info about Role Name"
							/>
						</Popover>
					</div>

					{/* Delegate AWSAccount IDs - Conditional rendering for disabled state */}
					<FormField
						label="Delegate AWSAccount IDs"
						description=""
						info={
							<Popover
								header="Delegate AWS Account IDs"
								content={
									<Box variant="small">
										AWS Account IDs that require delegated access to this
										project (auto-populated).
									</Box>
								}
								triggerType="custom"
							>
								<Button
									variant="icon"
									iconName="status-info"
									ariaLabel="Info about Delegate AWSAccount IDs"
								/>
							</Popover>
						}
					>
						{isKMSAutoPopulated ? (
							<Input // Use plain Cloudscape Input for fixed display
								value={tokenItems.map((item) => item.label).join(', ') || ''} // Join labels into a comma-separated string
								disabled={true} // Always disabled when auto-populated
								onChange={() => { }} // Provide an empty onChange for disabled Input to prevent console warnings
							/>
						) : (
							<>
								{' '}
								{/* Existing Input and TokenGroup for manual entry */}
								<Input
									value={text}
									onChange={({ detail }) => setText(detail.value)}
									onKeyDown={({ detail }) => {
										if (detail.key !== 'Enter') return
										if (text.trim() === '') return
										setTokenItems((prevItems) => [
											...prevItems,
											{
												label: text.trim(),
												dismissLabel: `Remove item ${text.trim()}`
											}
										])
										setText('')
									}}
									placeholder="Type here and press Enter"
								/>
								{tokenItems.length > 0 && (
									<TokenGroup
										items={tokenItems}
										onDismiss={({ detail: { itemIndex } }) => {
											setTokenItems((prevItems) => [
												...prevItems.slice(0, itemIndex),
												...prevItems.slice(itemIndex + 1)
											])
										}}
									/>
								)}
							</>
						)}
					</FormField>

					<FormField
						stretch
						label="Subnet Aliases"
						info={
							<Popover
								header="Subnet Aliases Configuration"
								content={
									<Box variant="small">
										Configure custom subnet aliases and their associated
										properties for your deployment.
									</Box>
								}
								triggerType="custom"
							>
								<Button
									variant="icon"
									iconName="status-info"
									ariaLabel="Info about Subnet Aliases section"
								/>
							</Popover>
						}
					>
						<div
							style={{
								width: '100%',
								minWidth: 0,
								boxSizing: 'border-box'
							}}
						>
							<Table
								trackBy="id"
								wrapLines
								columnDefinitions={[
									{
										id: 'alias',
										header: 'Subnet Alias',
										cell: (item) => (
											<Input
												value={item.alias}
												onChange={({ detail }) =>
													setSubnetAliasesItems((prev) =>
														prev.map((p) =>
															p.id === item.id ? { ...p, alias: detail.value } : p
														)
													)
												}
												placeholder="e.g. lb, rds"
											/>
										)
									},
									{
										id: 'count',
										header: 'Count',
										width: 100,
										cell: (item) => (
											<Input
												value={item.count}
												onChange={({ detail }) =>
													setSubnetAliasesItems((prev) =>
														prev.map((p) =>
															p.id === item.id ? { ...p, count: detail.value } : p
														)
													)
												}
												placeholder="Count"
											/>
										)
									},
									{
										id: 'identifier',
										header: 'Identifier',
										cell: (item) => (
											<Input
												value={item.identifier}
												onChange={({ detail }) =>
													setSubnetAliasesItems((prev) =>
														prev.map((p) =>
															p.id === item.id
																? { ...p, identifier: detail.value }
																: p
														)
													)
												}
												placeholder="e.g. tag:Purpose"
											/>
										)
									},
									{
										id: 'value',
										header: 'Value',
										cell: (item) => (
											<Input
												value={item.value}
												onChange={({ detail }) =>
													setSubnetAliasesItems((prev) =>
														prev.map((p) =>
															p.id === item.id ? { ...p, value: detail.value } : p
														)
													)
												}
												placeholder="e.g. WORKERNODE"
											/>
										)
									},
									{
										id: 'actions',
										header: '',
										width: 100,
										cell: (item) => {
											const rowIndex = subnetAliasesItems.findIndex(
												(r) => r.id === item.id
											)
											if (rowIndex <= 0) return null
											return (
												<Button
													variant="inline-icon"
													iconName="remove"
													ariaLabel="Remove subnet alias"
													onClick={() =>
														setSubnetAliasesItems((prev) =>
															prev.filter((p) => p.id !== item.id)
														)
													}
												>
													Remove
												</Button>
											)
										}
									}
								]}
								items={subnetAliasesItems}
								empty="No items"
							/>
						</div>
						<Box margin={{ top: 's' }}>
							<Button
								variant="normal"
								onClick={() =>
									setSubnetAliasesItems((prev) => [
										...prev,
										{
											id: `add-${Date.now()}`,
											alias: '',
											count: '',
											identifier: '',
											value: ''
										}
									])
								}
							>
								Add Subnet Aliases
							</Button>
						</Box>
					</FormField>

					<FormField label="TF Providers" stretch>
						<div
							style={{
								width: '100%',
								minWidth: 0,
								boxSizing: 'border-box'
							}}
						>
						<Table
							trackBy="id"
							wrapLines
							columnDefinitions={[
								{
									id: 'alias',
									header: 'Alias',
									width: 140,
									cell: (e) => {
										const isAdditional = e.id.startsWith('add-')
										if (!isAdditional) return breakableCellText(e.alias)
										return (
											<Input
												value={e.alias}
												onChange={({ detail }) =>
													setAdditionalTfProviders((prev) =>
														prev.map((p) =>
															p.id === e.id ? { ...p, alias: detail.value } : p
														)
													)
												}
												placeholder="e.g. environment"
											/>
										)
									}
								},
								{
									id: 'region',
									header: 'Region',
									width: 160,
									cell: (e) => {
										const isAdditional = e.id.startsWith('add-')
										if (!isAdditional) return breakableCellText(e.region)
										return (
											<Input
												value={e.region}
												onChange={({ detail }) =>
													setAdditionalTfProviders((prev) =>
														prev.map((p) =>
															p.id === e.id ? { ...p, region: detail.value } : p
														)
													)
												}
												placeholder="e.g. ap-southeast-1"
											/>
										)
									}
								},
								{
									id: 'arn',
									header: 'ARN',
									cell: (e) => {
										const isAdditional = e.id.startsWith('add-')
										if (!isAdditional) return breakableCellText(e.arn)
										return (
											<Input
												value={e.arn}
												onChange={({ detail }) =>
													setAdditionalTfProviders((prev) =>
														prev.map((p) =>
															p.id === e.id ? { ...p, arn: detail.value } : p
														)
													)
												}
												placeholder="arn:aws:iam::..."
											/>
										)
									}
								},
								{
									id: 'externalId',
									header: 'External ID',
									width: 200,
									cell: (e) => {
										const isAdditional = e.id.startsWith('add-')
										if (!isAdditional)
											return breakableCellText(e.externalId || '—')
										return (
											<Input
												value={e.externalId}
												onChange={({ detail }) =>
													setAdditionalTfProviders((prev) =>
														prev.map((p) =>
															p.id === e.id
																? { ...p, externalId: detail.value }
																: p
														)
													)
												}
												placeholder="External ID"
											/>
										)
									}
								},
								{
									id: 'actions',
									header: '',
									width: 100,
									cell: (e) => {
										const isAdditional = e.id.startsWith('add-')
										if (!isAdditional) return null
										return (
											<Button
												variant="inline-icon"
												iconName="remove"
												ariaLabel="Remove provider"
												onClick={() =>
													setAdditionalTfProviders((prev) =>
														prev.filter((p) => p.id !== e.id)
													)
												}
											>
												Remove
											</Button>
										)
									}
								}
							]}
							items={tfProvidersItems}
							empty="No providers"
						/>
						</div>
						<Box margin={{ top: 's' }}>
							<Button
								variant="normal"
								onClick={() =>
									setAdditionalTfProviders((prev) => [
										...prev,
										{
											id: `add-${Date.now()}`,
											alias: '',
											region: '',
											arn: '',
											externalId: ''
										}
									])
								}
							>
								Add Providers
							</Button>
						</Box>
					</FormField>

					{/* Security Aliases - Tooltip placement restored to FormTagEditor info prop */}
					<FormTagEditor
						label="Security Aliases"
						name="securityAliases"
						control={control}
						setFormValues={setFormValues}
						tags={[]}
						info={
							// Pass info prop to FormTagEditor's main label
							<Popover
								header="Security Aliases"
								content={
									<Box variant="small">
										Define aliases for security groups or related security
										configurations.
									</Box>
								}
								triggerType="custom"
							>
								<Button
									variant="icon"
									iconName="status-info"
									ariaLabel="Info about Security Aliases"
								/>
							</Popover>
						}
					/>

					{/* Additional Tags - Tooltip placement restored to FormTagEditor info prop */}
					<FormTagEditor
						label="Additonal Tags"
						name="tags"
						control={control}
						setFormValues={setFormValues}
						tags={[]}
						info={
							// Pass info prop to FormTagEditor's main label
							<Popover
								header="Additional Tags"
								content={
									<Box variant="small">
										Apply custom tags (key-value pairs) for resource
										identification and management.
									</Box>
								}
								triggerType="custom"
							>
								<Button
									variant="icon"
									iconName="status-info"
									ariaLabel="Info about Additional Tags"
								/>
							</Popover>
						}
					/>
				</SpaceBetween>
			</Container>
		</Form>
	)
}

export default ApprovalForm
